import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { useSearchParams } from "react-router-dom";

import { Tabs, TabData } from "src/base-components/Tabs";
import { CaseFiles } from "src/cases/caseView/CaseFiles";
import { CaseTasks } from "src/cases/caseView/CaseTasks";
import { CaseHistory } from "src/cases/caseView/history/CaseHistory";
import {
  useActiveCaseActionToast,
  useShouldShowTasksTab,
} from "src/cases/caseView/hooks";
import { RunnningCaseAction } from "src/cases/caseView/types";
import { useCase } from "src/cases/queries/cases";
import { EmptyState } from "src/design-system/EmptyState";
import { CasePageParams } from "src/router/urls";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { useParamsDecode } from "src/utils/useParamsDecode";

const TABS_PARAM_KEY = "tab";

const TABS = {
  tasks: "tasks",
  files: "files",
  history: "history",
} as const;

export const CaseSidebar: React.FC = () => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData, isLoading } = useCase(caseId);
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldShowTasks = useShouldShowTasksTab(caseData);

  const activeAction = caseData?.data?.running_actions?.at(0) as
    | RunnningCaseAction
    | undefined
    | null;

  useActiveCaseActionToast({
    activeAction,
    caseEtag: caseData?.data.etag,
    caseId: caseData?.data.id,
  });

  const tabData: TabData[] = [
    ...(shouldShowTasks
      ? [
          {
            key: TABS.tasks,
            label: "Tasks",
            content: <CaseTasks caseData={caseData} isLoading={isLoading} />,
          },
        ]
      : []),
    {
      key: TABS.files,
      label: "Files",
      content: <CaseFiles caseData={caseData} isLoading={isLoading} />,
    },
    {
      key: TABS.history,
      label: "History",
      content: (
        <ErrorBoundary
          fallbackErrorContent={
            <div className="flex h-full flex-col gap-y-3 p-4 pt-3">
              <EmptyState
                description="We're having trouble displaying the case history. Please refresh the page or contact support if the problem persists."
                headline="History Error"
                icon={faExclamationCircle}
                variant="error"
              />
            </div>
          }
        >
          <CaseHistory />
        </ErrorBoundary>
      ),
    },
  ];

  const defaultTab = shouldShowTasks ? TABS.tasks : TABS.files;

  return (
    <div
      className="h-full w-full bg-white"
      data-loc="case-sidebar-tab-list"
      data-ready={isLoading ? "false" : "true"}
    >
      <Tabs
        containerClassName="h-full w-full flex flex-col"
        panelClassName="h-full flex flex-col w-full"
        panelsClassName="flex-1 decideScrollbar overflow-y-auto flex w-full"
        selectedKey={searchParams.get(TABS_PARAM_KEY) ?? defaultTab}
        tabClassName="flex items-center after:bg-indigo-600 px-2"
        tabListClassName="flex h-12 mx-4 border-b border-gray-200 gap-4"
        tabs={tabData}
        onChange={(key) => {
          setSearchParams((prev) => {
            prev.set(TABS_PARAM_KEY, key);
            return prev;
          });
        }}
      />
    </div>
  );
};
